import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { EmvioCheckbox } from '@nexio/emvio-form-components';
import { doesPropExistAndHasItChanged, currencyMapper, getCurrencyCode } from '@nexio/emvio-util-app';
import _ from 'lodash';

import { merchantShape } from '../../../../utils/propTypes';
import './MerchantSelectorList.scss';
import { toast } from 'react-toastify';

export const MAX_MERCHANTS = 25;

export default class MerchantSelectorList extends Component {
    static propTypes = {
        isOpen: PropTypes.bool,
        merchants: PropTypes.arrayOf(merchantShape),
        onUpdateSelectedMerchants: PropTypes.func.isRequired,
        selectedMerchants: PropTypes.arrayOf(merchantShape),
        onToggleSelectorOpen: PropTypes.func.isRequired,
        paymentServiceMerchants: PropTypes.any
    };

    static defaultProps = {
        merchants: []
    };

    constructor(props) {
        super(props);

        this.state = {
            selectedMerchants: [],
            filteredMerchants: [],
            searchTerm: '',
            isVisible: this.props.isOpen
        };

        this.renderMerchant = this.renderMerchant.bind(this);
        this.handleSelectAllToggle = this.handleSelectAllToggle.bind(this);
        this.handleToggleMerchant = this.handleToggleMerchant.bind(this);
        this.getCurrencyCode = this.getCurrencyCode.bind(this);
        this.handleChangeSearch = this.handleChangeSearch.bind(this);
        this.handleSearchOnKeyDown = this.handleSearchOnKeyDown.bind(this);
        this.sortMerchantsMerge = this.sortMerchantsMerge.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (doesPropExistAndHasItChanged(prevProps, this.props, 'merchants')) {
            this.setState({ filteredMerchants: this.sortMerchantsMerge(this.props.merchants, this.props.paymentServiceMerchants) });
        }

        if (doesPropExistAndHasItChanged(prevProps, this.props, 'selectedMerchants')) {
            this.setState({ selectedMerchants: this.props.selectedMerchants });
        }

        if (prevProps.isOpen && !this.props.isOpen) {
            // wait for drawer to animate completely closed before saying not visible
            setTimeout(() => this.setState({ isVisible: false }), 300);

            this.props.onUpdateSelectedMerchants(this.state.selectedMerchants, this.state.selectedMerchants.length === this.props.merchants.length);
        }

        if (!prevProps.isOpen && this.props.isOpen) {
            this.setState({
                searchTerm: '',
                filteredMerchants: [
                    ...this.sortMerchantsMerge(this.state.selectedMerchants, this.props.paymentServiceMerchants),
                    ...this.sortMerchantsMerge(_.differenceWith(this.props.merchants, this.state.selectedMerchants, _.isEqual), this.props.paymentServiceMerchants)
                ],
                isVisible: true
            });
        }
    }

    sortMerchantsMerge(merchants, psMerchants) {
        const sortedMerchants = _.sortBy(merchants, (merchant) => {
            const name = _.get(merchant, 'name') || '';

            return name.toLowerCase();
        });

        return sortedMerchants.map((merchant) => {
            const paymentServiceMerchant = _.find(psMerchants, { merchantId: merchant.merchantId }) || {};
            _.set(merchant, 'enabled', _.get(paymentServiceMerchant, 'enabled'));

            return merchant;
        });
    }

    handleToggleMerchant(toggledMerchant, isChecked) {
        if (!isChecked) {
            if (_.size(this.state.selectedMerchants) >= MAX_MERCHANTS) {
                toast(`You may select a maximum of ${MAX_MERCHANTS} merchantIds.`, { type: 'error' });
            } else {
                this.setState({
                    selectedMerchants: [...this.state.selectedMerchants, toggledMerchant]
                });
            }
        } else {
            this.setState({
                selectedMerchants: this.state.selectedMerchants.filter((merchant) => {
                    return merchant.merchantId !== toggledMerchant.merchantId;
                })
            });
        }
    }

    handleSelectAllToggle() {
        const selectedMerchants = this.isSelectAllChecked() ?
            _.without(this.state.selectedMerchants, ...this.state.filteredMerchants) :
            _.slice(_.uniq([...this.state.selectedMerchants, ...this.state.filteredMerchants]), 0, MAX_MERCHANTS);

        this.setState({ selectedMerchants });
    }

    isSelectAllChecked() {
        const { selectedMerchants, filteredMerchants } = this.state;
        const { merchants } = this.props;
        if (_.size(selectedMerchants) >= _.size(merchants) || _.size(selectedMerchants) >= MAX_MERCHANTS) {
            return true;
        } else if (_.size(_.intersection(selectedMerchants, filteredMerchants)) === _.size(filteredMerchants) && !_.isEmpty(filteredMerchants)) {
            return true;
        }

        return false;
    }

    checkSameCurrencyId(merchantList) {
        const groupedMerchants = _.groupBy(merchantList, 'currencyId');

        return Object.keys(groupedMerchants).length === 1;
    }

    getCurrencyCode(currencyId) {
        if (!this.checkSameCurrencyId(this.props.merchants)) {
            return _.get(currencyMapper, `${currencyId}.code`, '???').toUpperCase();
        }
    }

    renderMerchant(merchant) {
        const isChecked = this.state.selectedMerchants.includes(merchant);

        return (
            <div className="merchant" key={merchant.merchantId}>
                <EmvioCheckbox
                    checked={isChecked}
                    onChange={() => this.handleToggleMerchant(merchant, isChecked)}
                />
                <div className="descriptor-area" onClick={() => this.handleToggleMerchant(merchant, isChecked)}>
                    <div className="descriptor">
                        <div className="emvio-mid-name" title={merchant.name}>{merchant.name}</div>
                        <div className="emvio-enabled-txt" title="Nexio Merchant Management Processing Status">
                            {merchant.enabled === false ? ' Disabled' : ''}
                            {merchant.enabled === true ? ' Enabled' : ''}
                        </div>
                    </div>
                    <div className="bottom-section">
                        <div className="merchant-id">
                            {merchant.merchantId}
                        </div>
                        <div className="currency-code">
                            {this.getCurrencyCode(merchant.currencyId)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    handleChangeSearch(event) {
        const searchTerm = event.target.value.toLowerCase();
        const filteredMerchants = this.props.merchants.filter((merchant) => {
            const mercantString = (merchant.name + merchant.merchantId + getCurrencyCode(merchant.currencyId)).toLowerCase();

            return mercantString.indexOf(searchTerm) !== -1;
        });
        this.setState({ searchTerm, filteredMerchants });
    }

    handleSearchOnKeyDown(e) {
        const escapeKeyCode = 27;

        if (e.keyCode === escapeKeyCode) {
            this.props.onToggleSelectorOpen();
        }
    }

    render() {
        const selectedMerchants = _.get(this, 'state.selectedMerchants') || [];

        const selectText = _.size(this.props.merchants) >= MAX_MERCHANTS ? `SELECT ALL (max ${MAX_MERCHANTS})` : 'SELECT ALL';

        return (
            <div className="emvio-merchant-selector-list">
                <div className="number-all-selected">
                    <span>
                        {selectedMerchants.length} {selectedMerchants.length === 1 ? 'Merchant' : 'Merchants'} Currently Selected
                    </span>
                </div>
                <div className="search-input">
                    <input
                        autoFocus
                        onKeyDown={this.handleSearchOnKeyDown}
                        onChange={this.handleChangeSearch}
                        placeholder="Filter Merchant List"
                        value={this.state.searchTerm}
                    />
                    <span className="icon-search" />
                </div>
                <div className="select-all">
                    <EmvioCheckbox
                        checked={this.isSelectAllChecked()}
                        isVisible={this.props.isOpen}
                        onChange={this.handleSelectAllToggle}
                    />
                    <div className="descriptor-area">
                        <span className="select-all-descriptor" onClick={this.handleSelectAllToggle}>{selectText}</span>
                    </div>
                </div>
                {this.state.filteredMerchants.length ? this.state.filteredMerchants.map(this.renderMerchant) : <div className="no-merchants-found">No Merchants Found</div>}
            </div>
        );
    }
}
